import React, { useEffect, useRef, useState } from 'react';
import Tooltip from '../general/Tooltip';
import NovaMark from './NovaMark';
import { useNova } from './NovaContext';
import { novaYellow } from './novaTheme';

// Matches the "hello" clip's own authored duration (public/nova/mark/*/hello.svg) —
// the asset itself loops, so it's held for exactly one play, then swapped back to
// the resting static mark, per spec: "Once per hover, no loop... clip ends — back
// to static."
const HELLO_DURATION_MS = 1625;

const NovaAvatar: React.FC = () => {
  const { isOpen, toggleNova } = useNova();
  const [playHello, setPlayHello] = useState(false);
  const helloTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(helloTimeoutRef.current), []);

  if (isOpen) return null;

  // Per spec, once triggered the clip always plays to completion — a quick
  // pass-through hover shouldn't cut it short, so leaving/blurring doesn't
  // cancel it, only starting it again (re-hovering mid-play) restarts the timer.
  const startHello = () => {
    clearTimeout(helloTimeoutRef.current);
    setPlayHello(true);
    helloTimeoutRef.current = setTimeout(() => setPlayHello(false), HELLO_DURATION_MS);
  };

  return (
    <div className="fixed right-6 bottom-6" style={{ zIndex: 999999 }}>
      <Tooltip placement="top" content="Ask Nova">
        <button
          type="button"
          onClick={() => toggleNova()}
          onMouseEnter={startHello}
          onFocus={startHello}
          aria-label="Open Nova assistant"
          className="relative flex cursor-pointer items-center justify-center rounded-full transition-transform hover:scale-105"
          style={{
            width: 70,
            height: 70,
            background: '#1B190F',
            boxShadow: '0 18px 50px -18px rgba(30, 28, 22, 0.5)',
          }}
        >
          {playHello ? (
            // public/nova/mark/*/hello.svg is exported on a 6000x6000 canvas with the
            // arms' resting silhouette filling only ~half of it (headroom for the
            // outward "explode" motion mid-clip) — the static glyph below has no such
            // padding, so matching its visible size here takes roughly double the size.
            <NovaMark state="hello" colourway="acid" size={72} />
          ) : (
            <NovaMark staticColor={novaYellow.high} animated={false} size={36} />
          )}
        </button>
      </Tooltip>
    </div>
  );
};

export default NovaAvatar;
